import { withBqnId } from "common/api";
import { createPromiseWithResolve, doesNothing } from "common/utils";

// The BQN serving the GUI, which its session calls the entry node. The
// commands run in it carry its "bqn-id ID" option, and the caller names them:
// the CLI takes the option in some commands only, and answers a syntax error
// to the rest of them.

const _CMD_BQN_ID = "show system bqn id";

const _BQN_ID_ERROR =
  "Cannot get current node's ID. Accept to reconnect.";

// Reloading again would not fix a BQN that does not answer its ID.
const _BQN_ID_ERROR_AGAIN =
  "Cannot get current node's ID after reconnecting. The data of the whole " +
  "cluster may be shown.";

// The command expires on its own after a minute, which disconnects the GUI.
// The commands waiting for the BQN are released well before that.
const _BQN_ID_TMOUT_MS = 15000;

// The reconnection already tried, kept over the reload of the GUI.
const _RECONNECT_NAME = "bqnIdReconnect";

// In standalone mode the command is not defined and takes no option.
const isSyntaxError = (error) => /Syntax error/.test(String(error));

const wasReconnected = () =>
  window.sessionStorage.getItem(_RECONNECT_NAME) !== null;

const reconnect = () => {
  window.sessionStorage.setItem(_RECONNECT_NAME, "yes");
  window.location.reload();
};

// Once the BQN is known, a later failure may reconnect again.
const forgetReconnect = () =>
  window.sessionStorage.removeItem(_RECONNECT_NAME);

// The reconnection is offered once: it is not offered again to the GUI that
// comes from one, so that the reload does not go on for ever.
const reportBqnIdError = () => {
  if (!window.showModalError) {
    return;
  }

  if (wasReconnected()) {
    window.showModalError("Error:", _BQN_ID_ERROR_AGAIN);
  } else {
    window.showModalError("Error:", _BQN_ID_ERROR, reconnect);
  }
};

export const parseBqnId = (response) => {
  const value = String(response).trim();
  const bqnId = Number(value);

  if (value === "" || !Number.isInteger(bqnId) || bqnId < 0) {
    throw new Error(`%ERR-EBADF: Invalid BQN ID: "${value}"`);
  }

  return bqnId;
};

// The command carries the option of the entry node, which has no ID to carry
// in standalone mode, and none either when it cannot be retrieved.
export const withEntryBqnId = (command, bqnId) =>
  bqnId === undefined || bqnId === null ? command : withBqnId(command, bqnId);

// The command to run in the entry node, as soon as its ID is known. The
// caller runs it, since only it knows how: run(), rush() or in a batch.
export const atEntryNode = (command) =>
  ifCl.getEntryBqnId().then((bqnId) => withEntryBqnId(command, bqnId));

// Keep the ID of the entry node in the CLI client, one per session.
export function inEntryNode(client) {
  // Commands of the client before it is decorated.
  const original = {
    login: client.login.bind(client),
    logout: client.logout.bind(client),
    run: client.run.bind(client),
  };

  // Replaced per session: no answer outlives the session that asked.
  let whenBqnIdReady;
  let resolveBqnIdReady;
  // Drops the answer of the command in flight, and its deadline.
  let abandonLoad = doesNothing;

  const asNoBqnId = (error) => {
    if (isSyntaxError(error)) {
      return null;
    }
    console.warn("Cannot get the BQN of the session:", error);

    return undefined;
  };

  const prepare = () => {
    [whenBqnIdReady, resolveBqnIdReady] = createPromiseWithResolve();
  };

  // Nothing waits for the BQN of a session that ended, and nothing is
  // reported for it either.
  const forget = () => {
    abandonLoad();
    resolveBqnIdReady(undefined);
    prepare();
  };

  // Sent once per session, when it can answer commands. The BQN is reported
  // as unknown once, whether the command fails or does not answer in time.
  const load = () => {
    // Only the last command asked for answers this session.
    abandonLoad();

    const answerOfSession = resolveBqnIdReady;
    let isAnswered = false;

    const answer = (bqnId) => {
      if (isAnswered) {
        return;
      }

      isAnswered = true;
      if (bqnId === undefined) {
        reportBqnIdError();
      } else {
        forgetReconnect();
      }
      answerOfSession(bqnId);
    };

    // The commands waiting are sent as they are when the BQN is too late.
    const expires = window.setTimeout(() => {
      console.warn(`Cannot get the BQN of the session: "${_CMD_BQN_ID}" `
        + `did not answer in ${_BQN_ID_TMOUT_MS} ms`);
      answer(undefined);
    }, _BQN_ID_TMOUT_MS);

    abandonLoad = () => {
      isAnswered = true;
      window.clearTimeout(expires);
    };

    return original
      .run(_CMD_BQN_ID)
      .then(parseBqnId)
      .catch(asNoBqnId)
      .then((bqnId) => {
        window.clearTimeout(expires);
        answer(bqnId);
      });
  };

  const login = (user, passwd, resCb) =>
    original.login(user, passwd, (response) => {
      const { LoginResponse: loginResponse } = response || {};

      if (loginResponse && loginResponse.code === 0) {
        load();
      }
      if (typeof resCb === "function") {
        resCb(response);
      }
    });

  const logout = (resCb) => {
    forget();

    return original.logout(resCb);
  };

  prepare();
  client.on("disconnected", forget);

  client.login = login;
  client.logout = logout;
  // A reloaded GUI recovers its session instead of logging in.
  client.loadBqnId = load;
  // The BQN of the session, as soon as it is known.
  client.getEntryBqnId = () => whenBqnIdReady;

  return client;
}

export default inEntryNode;
